import React, { useState, useEffect } from 'react';
import { ExportImportChart, SyncStatusBadge, ReconciliationSummary } from '../components/GridExchange';
import PlantSelector from '../components/Dashboard/PlantSelector';
import { meterExchangeService, MeterExchangeResponse, SyncValidationResponse } from '../services/meterExchangeService';

export const MeterExportImport: React.FC = () => {
  const [selectedPlant, setSelectedPlant] = useState('pspr_hindalga');
  const [dateRange, setDateRange] = useState('2024-10-01_to_2024-10-10');
  const [showExport, setShowExport] = useState(true);
  const [showImport, setShowImport] = useState(true);
  const [exchangeData, setExchangeData] = useState<MeterExchangeResponse | null>(null);
  const [syncData, setSyncData] = useState<SyncValidationResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedPlant, dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [exchangeResponse, syncResponse] = await Promise.all([
        meterExchangeService.getExportImportData(selectedPlant, dateRange),
        meterExchangeService.getSyncValidation(selectedPlant)
      ]);
      setExchangeData(exchangeResponse);
      setSyncData(syncResponse);
    } catch (error) {
      console.error('Error loading meter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format: 'csv' | 'png') => {
    if (!exchangeData) return;
    
    if (format === 'csv') {
      const csvContent = [
        'Date,Export (kWh),Import (kWh),Net Energy (kWh),Sync Status',
        ...exchangeData.chart_data.map(item => 
          `${item.date},${item.export_kwh},${item.import_kwh},${item.net_energy_kwh},${item.sync_status}`
        )
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `meter-exchange-${selectedPlant}-${dateRange}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meter Export/Import</h1>
          <p className="text-gray-600">Grid interaction and energy reconciliation monitoring</p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => handleExport('csv')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plant</label>
            <PlantSelector 
              selectedPlant={selectedPlant} 
              onPlantChange={setSelectedPlant} 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="2024-10-01_to_2024-10-10">Oct 1-10, 2024</option>
              <option value="2024-09-01_to_2024-09-30">September 2024</option>
              <option value="2024-08-01_to_2024-08-31">August 2024</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metrics</label>
            <div className="flex gap-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showExport}
                  onChange={(e) => setShowExport(e.target.checked)}
                  className="mr-2"
                />
                Export
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showImport}
                  onChange={(e) => setShowImport(e.target.checked)}
                  className="mr-2"
                />
                Import
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {exchangeData && syncData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ReconciliationSummary
                totalExport={exchangeData.summary.total_export_kwh}
                totalImport={exchangeData.summary.total_import_kwh}
                netExport={exchangeData.summary.net_export_kwh}
                syncIssuesCount={exchangeData.summary.sync_issues_count}
              />
            </div>
            
            <SyncStatusBadge
              syncPercentage={syncData.sync_validation.sync_percentage}
              status={syncData.sync_validation.status}
              syncedDays={syncData.sync_validation.synced_days}
              totalDays={syncData.sync_validation.total_days_checked}
            />
          </div>

          {/* Chart */}
          <ExportImportChart
            data={exchangeData.chart_data}
            showExport={showExport}
            showImport={showImport}
          />
        </>
      )}
    </div>
  );
};